import { useEffect, useRef, useState } from "react";
import MeetingMediaRoom from "@/components/MeetingMediaRoom";
import axios from "axios";
import { API, formatApiError } from "@/contexts/AuthContext";

export default function MeetingWaitingRoom({ event }) {
  const [room, setRoom] = useState(null);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recipient, setRecipient] = useState("");
  const [mailNotice, setMailNotice] = useState("");
  const mailRequest = useRef(null);
  const sequence = useRef(0);
  useEffect(() => () => { sequence.current += 1; }, []);
  async function act(action, guestId) {
    if (busy) return;
    if (action === "invite" && !window.confirm("Create a new private invitation? This invalidates any previous link and its guest sessions.")) return;
    if (action === "revoke" && !window.confirm("Revoke the invitation and all its guest sessions?")) return;
    const current = ++sequence.current;
    setBusy(true); setError("");
    if (action === "invite" || action === "revoke") { setLink(""); setMailNotice(""); mailRequest.current = null; }
    try {
      const base = API + "/meetings/events/" + encodeURIComponent(event.id);
      if (action === "load") {
        const { data } = await axios.get(base + "/waiting-room");
        if (current === sequence.current) setRoom(data);
      } else {
        const revision = room?.revision ?? event.revision;
        if (action === "invite") {
          const { data } = await axios.post(base + "/guest-invitation",
            { expected_revision: revision, expires_at: event.end_at });
          if (current === sequence.current) {
            setLink(window.location.origin + "/meetings/join/" + encodeURIComponent(event.id) + "#invite=" + data.invitation_token);
            setRoom({ revision: data.revision, guests: [] });
          }
        } else if (action === "revoke") {
          const { data } = await axios.delete(base + "/guest-invitation", { data: { expected_revision: revision } });
          if (current === sequence.current) setRoom({ revision: data.revision, guests: [] });
        } else {
          const { data } = await axios.post(base + "/guests/" + encodeURIComponent(guestId) + "/decision",
            { expected_revision: revision, decision: action });
          if (current === sequence.current) setRoom((previous) => ({ ...previous, revision: data.revision,
            guests: previous.guests.map((guest) => guest.guest_id === guestId ? { ...guest, status: data.status } : guest) }));
        }
      }
    } catch (failure) {
      if (current === sequence.current) setError(formatApiError(failure.response?.data?.detail || failure.message));
    } finally { if (current === sequence.current) setBusy(false); }
  }
  async function sendInvitation(formEvent) {
    formEvent.preventDefault();
    if (busy || !link || !recipient.trim()) return;
    if (!window.confirm("Send this private meeting invitation to " + recipient.trim() + "?")) return;
    const token = new URL(link).hash.slice("#invite=".length);
    if (mailRequest.current && mailRequest.current.recipient !== recipient.trim()) {
      if (!window.confirm("Start a separate invitation email for this recipient? The previous request may already have been sent.")) return;
      mailRequest.current = null;
    }
    mailRequest.current ||= { request_id: window.crypto.randomUUID(), recipient: recipient.trim() };
    const current = ++sequence.current;
    setBusy(true); setError(""); setMailNotice("");
    try {
      const { data } = await axios.post(API + "/meetings/events/" + encodeURIComponent(event.id) + "/invitation-email",
        { ...mailRequest.current, invitation_token: token });
      if (current !== sequence.current) return;
      setMailNotice(data.status === "provider_accepted"
        ? "The email provider accepted this invitation. Delivery to the inbox is not yet confirmed."
        : data.status === "cancelled" ? "This request was cancelled because the meeting changed."
        : "Delivery is unconfirmed. This request will not be automatically resent; check its status before creating another.");
    } catch (failure) {
      if (current === sequence.current) setError(formatApiError(failure.response?.data?.detail || failure.message));
    } finally { if (current === sequence.current) setBusy(false); }
  }
  return <section className="space-y-3 border-t pt-3" aria-label="Guest waiting room">
    <h3 className="font-medium">Guest waiting room</h3>
    <MeetingMediaRoom key={event.id} eventId={event.id} />
    <p className="text-xs text-slate-600">Guest participation remains disabled until activation. Creating a link does not send an email; sending requires the separate email action.</p>
    {error && <p role="alert" className="text-sm">{error}</p>}
    <div className="flex flex-wrap gap-2">
      <button type="button" disabled={busy} onClick={() => act("load")} className="rounded border px-3 py-1">Refresh guests</button>
      <button type="button" disabled={busy} onClick={() => act("invite")} className="rounded border px-3 py-1">New private invitation</button>
      <button type="button" disabled={busy} onClick={() => act("revoke")} className="rounded border px-3 py-1">Revoke invitation</button>
    </div>
    {link && <label className="block text-sm">Private link · expires at meeting end
      <input readOnly type="text" value={link} autoComplete="off" spellCheck={false}
        onFocus={(event) => event.target.select()} className="mt-1 w-full rounded border p-2" />
      <span className="text-xs">Share only with intended guests. Each guest still requires admission.</span>
    </label>}
    {link && <form onSubmit={sendInvitation} className="space-y-2">
      <label className="block text-sm">Guest email
        <input type="email" required maxLength={254} value={recipient} disabled={busy}
          onChange={(event) => setRecipient(event.target.value)} className="mt-1 block w-full rounded border p-2" />
      </label>
      <button type="submit" disabled={busy || !recipient.trim()} className="rounded border px-3 py-2">Send invitation email</button>
    </form>}
    {mailNotice && <p role="status" className="text-sm">{mailNotice}</p>}
    {room && room.guests.length === 0 && <p className="text-sm">No guests in this invitation.</p>}
    {room?.guests.map((guest) => <div key={guest.guest_id} className="flex flex-wrap items-center gap-2 text-sm">
      <span>{guest.display_name} · {guest.status}</span>
      {guest.status === "waiting" && <button type="button" disabled={busy} onClick={() => act("admitted", guest.guest_id)} className="rounded border px-2 py-1">Admit</button>}
      {["waiting", "admitted"].includes(guest.status) && <button type="button" disabled={busy} onClick={() => act("rejected", guest.guest_id)} className="rounded border px-2 py-1">
        {guest.status === "admitted" ? "Remove" : "Decline"}
      </button>}
    </div>)}
  </section>;
}
